import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { v4 as uuidv4 } from 'uuid'

export interface CartItem {
  id: string // Unique identifier for each cart item
  productId: string
  quantity: number
  selectedColor?: string
}

interface Product {
  id: string
  name: string
  description?: string
  concentration?: string
  price_per_kg?: number
  moq_grams?: number
  price_chart?: Record<string, number>
  slug?: string
  kg_price?: boolean
}

interface CartState {
  // Core state
  items: CartItem[]
  guestId: string | null
  isInitialized: boolean 
  lastSynced: number
  isSyncing: boolean
  syncError: string | null
  
  // Server-provided data
  initialProducts: Product[] | null
  translatedSlugs: Record<string, string> | null
  translatedNames: Record<string, string> | null
  
  // Derived data
  itemCount: number
  
  // Actions
  addItem: (productId: string, quantity: number, selectedColor?: string) => void
  updateItemQuantity: (productId: string, quantity: number, selectedColor?: string) => void
  removeItem: (productId: string, selectedColor?: string) => void
  clearCart: () => void
  initializeCart: (userId?: string) => Promise<void>
  syncCart: () => Promise<void>
  setError: (error: string | null) => void
  setSyncing: (isSyncing: boolean) => void
}

// Add a new interface for checkout information
export interface ShippingAddress {
  company_name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  notes?: string;
  vat_number?: string;
}

// Add a helper to check if we're in a browser environment
const isBrowser = typeof window !== 'undefined'

interface CheckoutState {
  // Shipping information
  email: string;
  phone: string;
  companyName: string;
  vatNumber: string;
  vatValid: boolean;
  vatValidatedCompanyName: string;
  shippingAddress: ShippingAddress;
  billingAddress: ShippingAddress;
  billingIsSameAsShipping: boolean;
  preferredPaymentMethod: 'wire' | 'crypto' | 'truevo';
  
  // Actions
  updateEmail: (email: string) => void;
  updatePhone: (phone: string) => void;
  updateCompanyName: (companyName: string) => void;
  updateVat: (vatNumber: string, isValid: boolean, validatedCompanyName?: string) => void;
  updateShippingAddress: (address: Partial<ShippingAddress>) => void;
  updateBillingAddress: (address: Partial<ShippingAddress>) => void;
  updateBillingIsSameAsShipping: (isSame: boolean) => void;
  updatePaymentMethod: (method: 'wire' | 'crypto' | 'truevo') => void;
  resetCheckout: () => void;
}

// Helper to normalize selectedColor
function normalizeSelectedColor(selectedColor?: string | null): string | undefined {
  return selectedColor ?? undefined;
}

// Helper to merge cart items by productId and selectedColor
function mergeCartItems(items: CartItem[]): CartItem[] {
  const map = new Map<string, CartItem>();
  for (const item of items) {
    const key = `${item.productId}:${normalizeSelectedColor(item.selectedColor) ?? ''}`;
    if (map.has(key)) {
      map.get(key)!.quantity += item.quantity;
    } else {
      map.set(key, { ...item, selectedColor: normalizeSelectedColor(item.selectedColor) });
    }
  }
  return Array.from(map.values());
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      // Initial state
      items: [],
      guestId: null,
      isInitialized: false,
      lastSynced: 0,
      isSyncing: false,
      syncError: null,
      initialProducts: null,
      translatedSlugs: null,
      translatedNames: null,
      
      // Derived data
      get itemCount() {
        return get().items.length
      },
      
      // Actions
      addItem: (productId, quantity, selectedColor) => {
        const normColor = normalizeSelectedColor(selectedColor);
        let items = [...get().items];
        const existingIndex = items.findIndex(item =>
          item.productId === productId &&
          normalizeSelectedColor(item.selectedColor) === normColor
        );
        if (existingIndex >= 0) {
          items[existingIndex].quantity += quantity;
        } else {
          items.push({
            id: uuidv4(),
            productId,
            quantity,
            selectedColor: normColor
          });
        }
        items = mergeCartItems(items);
        set({ items, lastSynced: Date.now() });
        get().syncCart();
      },
      
      updateItemQuantity: (productId, quantity, selectedColor) => {
        const normColor = normalizeSelectedColor(selectedColor);
        let items = [...get().items];
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productId);
        let existingIndex = -1;
        if (isUuid) {
          existingIndex = items.findIndex(item => item.id === productId);
        }
        if (existingIndex < 0) {
          existingIndex = items.findIndex(item =>
            item.productId === productId &&
            normalizeSelectedColor(item.selectedColor) === normColor
          );
        }
        if (existingIndex >= 0) {
          if (quantity <= 0) {
            items.splice(existingIndex, 1);
          } else {
            items[existingIndex].quantity = quantity;
          }
          items = mergeCartItems(items);
          set({ items, lastSynced: Date.now() });
          get().syncCart();
        }
      },
      
      removeItem: (productId, selectedColor) => {
        const normColor = normalizeSelectedColor(selectedColor);
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productId);
        let items;
        if (isUuid) {
          items = get().items.filter(item => item.id !== productId);
        } else {
          items = get().items.filter(item =>
            !(item.productId === productId && normalizeSelectedColor(item.selectedColor) === normColor)
          );
        }
        items = mergeCartItems(items);
        set({ items, lastSynced: Date.now() });
        get().syncCart();
      },
      
      clearCart: () => {
        set({ items: [], lastSynced: Date.now() })
        get().syncCart()
      },
      
      initializeCart: async (userId) => {
        if (get().isInitialized) return;
        try {
          set({ isSyncing: true });
          const guestId = get().guestId || uuidv4();
          if (isBrowser) {
            try {
              const response = await fetch('/api/cart', {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  ...(userId ? { 'X-User-ID': userId } : {})
                }
              });
              if (response.ok) {
                const data = await response.json();
                const existingItems = get().items || [];
                const serverItems = data.items || [];
                let mergedItems = [...existingItems];
                serverItems.forEach((serverItem: CartItem) => {
                  const existingIndex = mergedItems.findIndex(item =>
                    item.productId === serverItem.productId &&
                    normalizeSelectedColor(item.selectedColor) === normalizeSelectedColor(serverItem.selectedColor)
                  );
                  if (existingIndex >= 0) {
                    mergedItems[existingIndex].quantity = serverItem.quantity;
                  } else {
                    mergedItems.push({ ...serverItem, selectedColor: normalizeSelectedColor(serverItem.selectedColor) });
                  }
                });
                mergedItems = mergeCartItems(mergedItems);
                set({
                  items: mergedItems,
                  initialProducts: data.initialProducts || get().initialProducts,
                  translatedSlugs: data.translatedSlugs || get().translatedSlugs,
                  translatedNames: data.translatedNames || get().translatedNames
                });
              }
            } catch {}
          }
          set({
            guestId,
            isInitialized: true,
            lastSynced: Date.now(),
            syncError: null
          });
        } catch {
          set({
            syncError: 'Failed to initialize cart',
            isInitialized: true,
          });
        } finally {
          set({ isSyncing: false });
        }
      },
      
      syncCart: async () => {
        // Skip sync if not in browser
        if (!isBrowser) return
        
        const { items, isSyncing, guestId } = get()
        
        // Debounce syncing (don't start a new sync if one is in progress)
        if (isSyncing) return
        
        try {
          set({ isSyncing: true })
          
          // First, attempt to get current user ID
          let userId = null
          try {
            const response = await fetch('/api/auth/session', {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' }
            })
            
            if (response.ok) {
              const data = await response.json()
              userId = data?.userId || null
            }
          } catch {
            // Continue with guestId if session fetch fails
          }
          
          // POST cart to server, including either userId or guestId
          const response = await fetch('/api/cart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              items, 
              guestId,
              userId // Include userId if available
            }),
          })
          
          if (!response.ok) throw new Error('Failed to sync cart')
          
          set({ lastSynced: Date.now(), syncError: null })
        } catch {
          set({ syncError: 'Failed to sync cart' })
        } finally {
          set({ isSyncing: false })
        }
      },
      
      setError: (error) => set({ syncError: error }),
      setSyncing: (isSyncing) => set({ isSyncing })
    }),
    {
      name: 'cart-storage',
      storage: createJSONStorage(() => {
        // Only use localStorage in the browser
        if (isBrowser) {
          return localStorage
        }
        // Return a dummy storage for SSR
        return {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {}
        }
      }),
      // Persist more fields to ensure they're available after reload
      partialize: (state) => ({ 
        items: state.items,
        guestId: state.guestId,
        initialProducts: state.initialProducts,
        translatedSlugs: state.translatedSlugs,
        translatedNames: state.translatedNames
      }),
      // Skip hydration to avoid SSR/client mismatch issues
      skipHydration: true
    }
  )
)

export const useCheckoutStore = create<CheckoutState>()(
  persist(
    (set) => ({
      // Initial state
      email: '',
      phone: '',
      companyName: '',
      vatNumber: '',
      vatValid: false,
      vatValidatedCompanyName: '',
      shippingAddress: {
        company_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        notes: ''
      },
      billingAddress: {
        company_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        notes: ''
      },
      billingIsSameAsShipping: true,
      preferredPaymentMethod: 'truevo',
      
      // Actions
      updateEmail: (email) => set({ email }),
      updatePhone: (phone) => set({ phone }),
      updateCompanyName: (companyName) => set({ companyName }),
      updateVat: (vatNumber, isValid, validatedCompanyName) => set({ vatNumber, vatValid: isValid, vatValidatedCompanyName: validatedCompanyName }),
      updateShippingAddress: (address) => set((state) => ({ 
        shippingAddress: { ...state.shippingAddress, ...address }
      })),
      updateBillingAddress: (address) => set((state) => ({ 
        billingAddress: { ...state.billingAddress, ...address }
      })),
      updateBillingIsSameAsShipping: (isSame) => set({ billingIsSameAsShipping: isSame }),
      updatePaymentMethod: (method) => set({ preferredPaymentMethod: method }),
      resetCheckout: () => set({
        email: '',
        phone: '',
        companyName: '',
        vatNumber: '',
        vatValid: false,
        vatValidatedCompanyName: '',
        shippingAddress: {
          company_name: '',
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: '',
          notes: ''
        },
        billingAddress: {
          company_name: '',
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: '',
          notes: ''
        },
        billingIsSameAsShipping: true
      })
    }),
    {
      name: 'checkout-storage',
      storage: createJSONStorage(() => {
        // Only use localStorage in the browser
        if (isBrowser) {
          return localStorage
        }
        // Return a dummy storage for SSR
        return {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {}
        }
      }),
      // Skip hydration to avoid SSR/client mismatch issues
      skipHydration: true,
      // Add partialize to ensure all fields are properly stored
      partialize: (state) => ({
        email: state.email,
        phone: state.phone,
        companyName: state.companyName,
        vatNumber: state.vatNumber,
        vatValid: state.vatValid,
        vatValidatedCompanyName: state.vatValidatedCompanyName,
        shippingAddress: state.shippingAddress,
        billingAddress: state.billingAddress,
        billingIsSameAsShipping: state.billingIsSameAsShipping,
        preferredPaymentMethod: state.preferredPaymentMethod
      })
    }
  )
) 